import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./PackageOrders.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://backend.washta.com";
const ORDERS_URL = `${API_BASE}/api/residential/admin/orders`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("accessTokenAdmin")}` });

const PERIODS = ["Today", "This week", "This month", "Custom"];
const STATUSES = ["active", "low_delivery", "expired", "cancelled", "completed"];
const STATUS_LABEL = { active: "Active", low_delivery: "Low delivery", expired: "Expired", cancelled: "Cancelled", completed: "Completed" };

const todayISO = () => new Date().toISOString().slice(0, 10);
const weekStartISO = () => {
  const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10);
};
const monthStartISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };

function periodDates(period) {
  const today = todayISO();
  if (period === "Today") return { from: today, to: today };
  if (period === "This week") return { from: weekStartISO(), to: today };
  if (period === "This month") return { from: monthStartISO(), to: today };
  return {};
}

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate()} ${dt.toLocaleString("en", { month: "short" })} ${dt.getFullYear()}`;
};

const fmtTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
};

/* ─── TopBar ─────────────────────────────────────────────────────────────── */
function TopBar() {
  return (
    <div className="po-topbar">
      <div>
        <h1 className="sap-title">Package Orders</h1>
        <p className="po-subtitle">
          Every package sold, across all providers and locations. Full financial record with superadmin intervention.
        </p>
      </div>
    </div>
  );
}

/* ─── FilterPanel ────────────────────────────────────────────────────────── */
function FilterPanel({ filters, setFilters, onSearch, loading }) {
  const update = (key, val) => setFilters((p) => ({ ...p, [key]: val }));

  const handlePeriod = (p) => {
    const dates = periodDates(p);
    setFilters((prev) => ({ ...prev, period: p, from: dates.from || prev.from, to: dates.to || prev.to }));
  };

  return (
    <div className="po-filters">
      <div className="po-filter-row">
        <span className="po-filter-label">Period</span>
        <div className="po-period-tabs">
          {PERIODS.map((p) => (
            <button key={p} className={`po-period-tab ${filters.period === p ? "active" : ""}`} onClick={() => handlePeriod(p)}>{p}</button>
          ))}
        </div>
        <input type="date" className="po-date-input" style={{ width: "18%" }} value={filters.from} onChange={(e) => update("from", e.target.value)} />
        <span className="po-arrow">→</span>
        <input type="date" className="po-date-input" style={{ width: "18%" }} value={filters.to} onChange={(e) => update("to", e.target.value)} />
        <button className="po-clear-all" onClick={() => setFilters({ period: "This week", from: weekStartISO(), to: todayISO(), location: "", provider: "", status: "", washCount: "", query: "" })}>Clear all</button>
      </div>

      <div className="po-filter-row">
        <span className="po-filter-label">Filter</span>
        <input className="po-select" placeholder="Location ID" value={filters.location} onChange={(e) => update("location", e.target.value)} />
        {/* <input className="po-select" placeholder="Provider ID" value={filters.provider} onChange={(e) => update("provider", e.target.value)} /> */}
        <select className="po-select" value={filters.status} onChange={(e) => update("status", e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select className="po-select" value={filters.washCount} onChange={(e) => update("washCount", e.target.value)}>
          <option value="">All packages</option>
          {[4, 8, 12, 16, 20].map((n) => <option key={n} value={n}>{n} / month</option>)}
        </select>
      </div>

      <div className="po-filter-row">
        <input
          className="po-text-input"
          placeholder="Order ID / plate / customer"
          value={filters.query}
          onChange={(e) => update("query", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
        />
        <button className="po-btn violet" onClick={onSearch} disabled={loading}>
          {loading ? "Loading…" : "Search"}
        </button>
      </div>
    </div>
  );
}

/* ─── StatusBadge ────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const cls = { active: "green", low_delivery: "red", expired: "gray", cancelled: "red", completed: "green" };
  return <span className={`po-badge ${cls[status] || "gray"}`}>{STATUS_LABEL[status] || status}</span>;
}

/* ─── OrdersTable ────────────────────────────────────────────────────────── */
function OrdersTable({ orders, selectedId, onToggle, loading }) {
  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#9aa1ac" }}>Loading orders…</div>;

  return (
    <div className="po-table-wrap">
      <table className="po-table">
        <thead>
          <tr>
            <th>Order</th><th>Customer</th><th>Vehicle</th><th>Location</th><th>Tier</th>
            <th>Car Type</th><th>Days</th><th>Delivered</th><th>Period</th>
            <th>Paid</th><th>Washta Net</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr className="po-empty-row"><td colSpan={13}>No orders match these filters.</td></tr>
          )}
          {orders.map((o) => {
            const isSelected = selectedId === o._id;
            const isLow = o.computedStatus === "low_delivery";
            return (
              <tr key={o._id} className={isSelected ? "selected" : ""} onClick={() => onToggle(o)}>
                <td><div className="po-order-id">{o.orderId || o._id}</div></td>
                <td>
                  <div>{o.customerName || "—"}</div>
                  <div className="po-customer-phone">{o.customerEmail || o.customerPhone || ""}</div>
                </td>
                <td>
                  <div>{o.vehicleLabel || o.vehicleModel || o.vehicleManufacturer || o.vehicleType || "—"}</div>
                  <div className="po-customer-phone">{o.vehiclePlateNumber || o.plateNumber || ""}</div>
                </td>
                <td>{o.locationDisplayName}</td>
                <td><span className="po-chip">{o.tierDisplayName || `${o.washCount} / month`}</span></td>
                <td><span className="po-chip car">{o.carType}</span></td>
                <td className="po-days">{o.recurringDaysLabel || (o.recurringDays || []).join(" · ")}</td>
                <td className={`po-delivered ${isLow ? "low" : ""}`}>{o.delivered || `${o.washesCompleted} / ${o.washCount}`}</td>
                <td>{o.period || `${fmtDate(o.startDate)} – ${fmtDate(o.endDate)}`}</td>
                <td className="po-money">AED {(o.totalAmount || 0).toFixed(2)}</td>
                <td className="po-money">AED {(o.washatNet ?? o.commissionAmount ?? 0).toFixed(2)}</td>
                <td><StatusBadge status={o.computedStatus || o.status} /></td>
                <td>
                  <button className={`po-open-link ${isSelected ? "open" : ""}`} onClick={(e) => { e.stopPropagation(); onToggle(o); }}>
                    <span className="chev">▾</span> {isSelected ? "Close" : "Open"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── MoneyBreakdown ─────────────────────────────────────────────────────── */
function MoneyBreakdown({ money }) {
  if (!money) return null;
  return (
    <div className="po-detail-col">
      <div className="po-detail-col-title">Money</div>
      <div className="po-money-row"><span className="label">Package price</span><span className="value">AED {(money.packagePrice || 0).toFixed(2)}</span></div>
      <div className="po-money-row"><span className="label">
        Platform fee (5%, cap 5.00)
        </span>
        <span className="value">AED {(money.platformFee || 0).toFixed(2)}</span></div>
      <div className="po-money-row strong"><span className="label">Customer charged</span><span className="value">AED {(money.customerCharged || 0).toFixed(2)}</span></div>
      <div className="po-money-row"><span className="label">Commission @ {money.commissionPct || 8}%</span><span className="value positive">AED {(money.commission || 0).toFixed(2)}</span></div>
      <div className="po-money-row"><span className="label">{money.stripeFeeLabel || "Stripe fee"}</span><span className="value negative">− AED {(money.stripeFee || 0).toFixed(2)}</span></div>
      <div className="po-money-row strong"><span className="label">Washta net</span><span className="value">AED {(money.washatNet ?? money.washtaNet ?? 0).toFixed(2)}</span></div>
      <div className="po-money-row">
        <span className="label">Provider payout</span>
        <span className="value">
          AED {(money.providerPayout || 0).toFixed(2)}{" "}
          <span className={`po-badge ${money.providerPayoutStatus === "released" ? "green" : "gray"} po-inline-status`}>
            {money.providerPayoutStatus === "released" ? "Released" : "Pending"}
          </span>
        </span>
      </div>
    </div>
  );
}

/* ─── DeliveryTimeline ───────────────────────────────────────────────────── */
function DeliveryTimeline({ washLog }) {
  if (!washLog?.length) return <div className="po-detail-col"><div className="po-detail-col-title">Delivery</div><p style={{ color: "#9aa1ac", fontSize: 13 }}>No wash log entries.</p></div>;

  const done = washLog.filter((w) => w.status === "completed").length;
  const badgeClass = { completed: "green", missed: "red", due_today: "due", scheduled: "scheduled" };
  const icon = { completed: "✓ ", missed: "✕ ", due_today: "", scheduled: "" };

  return (
    <div className="po-detail-col">
      <div className="po-detail-col-title">Delivery — {done} of {washLog.length} washes</div>
      {washLog.map((w, i) => (
        <div className="po-delivery-row" key={w.washId || i}>
          <span className="po-delivery-date">{fmtDate(w.scheduledDate)}</span>
          <span className={`po-badge po-delivery-status ${w.completedVia === "superadmin" && w.status === "completed" ? "completed-admin" : badgeClass[w.status] || "gray"}`}>
            {icon[w.status]}{w.status === "due_today" ? "Due today" : w.status === "completed" ? "Completed" : w.status === "missed" ? "Missed" : "Scheduled"}
          </span>
          <span className="po-delivery-note">{w.providerLabel || (w.status === "scheduled" ? "—" : "")}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── OrderDetailPanel ───────────────────────────────────────────────────── */
function OrderDetailPanel({ order, detail, detailLoading, onAction, actionLoading }) {
  const cancelled = order.status === "cancelled";
  const washLog = detail?.washLogEnriched || [];
  const hasDueToday = washLog.some((w) => w.status === "due_today");
  const hasCompleted = washLog.some((w) => w.status === "completed");

  return (
    <div className="po-detail">
      <div className="po-detail-header">
        <div className="po-detail-title">
          {order.orderId} — {order.vehicleLabel || order.vehicleModel || order.vehicleManufacturer || order.vehicleType || order.vehiclePlateNumber || order.plateNumber || "Vehicle"} · {order.locationDisplayName} · {order.tierDisplayName || `${order.washCount} / month`} washes
        </div>
        <div className="po-detail-sub">Purchased {fmtDate(order.createdAt)} · {fmtTime(order.createdAt)}</div>
      </div>

      {detailLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "#9aa1ac" }}>Loading detail…</div>
      ) : (
        <div className="po-detail-body">
          <MoneyBreakdown money={detail?.money} />
          <DeliveryTimeline washLog={washLog} />
        </div>
      )}

      <div className="po-detail-footer">
        <div className="po-footer-note">All actions are logged with admin name, timestamp and reason code. Records are never deleted.</div>
        <div className="po-footer-actions">
          <button className="po-btn violet" disabled={cancelled || !hasDueToday || actionLoading} onClick={() => onAction("complete")}>✓ Mark wash complete</button>
          <button className="po-btn" disabled={cancelled || !hasDueToday || actionLoading} onClick={() => onAction("missed")}>✕ Mark missed</button>
          <button className="po-btn" disabled={cancelled || !hasCompleted || actionLoading} onClick={() => onAction("reverse")}>↩ Reverse completion</button>
          <button className="po-btn" disabled={cancelled || actionLoading} onClick={() => onAction("reschedule")}>🗓 Reschedule wash</button>
          <button className="po-btn" disabled={cancelled || actionLoading} onClick={() => onAction("compensation")}>+ Add compensation wash</button>
          <button className="po-btn" disabled={cancelled || actionLoading} onClick={() => onAction("extend")}>🗓 Extend end date</button>
          <button className="po-btn outline-red" disabled={cancelled || actionLoading} onClick={() => onAction("cancel")}>⊘ Cancel package</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Root ───────────────────────────────────────────────────────────────── */
export default function PackageOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ period: "This week", from: weekStartISO(), to: todayISO(), location: "", provider: "", status: "", washCount: "", query: "" });

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2600); };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.location) params.locationId = filters.location;
      if (filters.provider) params.sellerId = filters.provider;
      if (filters.status) params.status = filters.status;
      if (filters.washCount) params.washCount = filters.washCount;
      if (filters.query.trim()) params.search = filters.query.trim();

      const res = await axios.get(ORDERS_URL, { headers: authHeader(), params });
      const data = res?.data?.data;
      setOrders(Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Auto-fetch when period/filter dropdowns change (not on every keystroke)
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const autoFields = ["from", "to", "location", "provider", "status", "washCount", "period"];
    const changed = autoFields.some((k) => prev[k] !== filters[k]);
    if (changed) fetchOrders();
    prevFiltersRef.current = filters;
  }, [filters, fetchOrders]);

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${ORDERS_URL}/${id}`, { headers: authHeader() });
      setDetail(res?.data?.data);
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to load order detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleOrder = (order) => {
    if (selectedOrder?._id === order._id) { setSelectedOrder(null); setDetail(null); return; }
    setSelectedOrder(order);
    fetchDetail(order._id);
  };

  const runAction = async (fn) => {
    setActionLoading(true);
    try {
      await fn();
      await Promise.all([fetchDetail(selectedOrder._id), fetchOrders()]);
      showToast("Action applied successfully");
    } catch (err) {
      showToast(err?.response?.data?.error || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = (action) => {
    const id = selectedOrder._id;
    const washLog = detail?.washLogEnriched || [];

    if (action === "complete") {
      const dueWash = washLog.find((w) => w.status === "due_today");
      if (!dueWash) return showToast("No due wash found for today");
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/wash/${dueWash.washId}/complete`, { reasonCode: "superadmin_confirmed" }, { headers: authHeader() }));
    } else if (action === "missed") {
      const dueWash = washLog.find((w) => w.status === "due_today");
      if (!dueWash) return showToast("No due wash found for today");
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/wash/${dueWash.washId}/miss`, { reasonCode: "superadmin_flagged" }, { headers: authHeader() }));
    } else if (action === "reverse") {
      const lastCompleted = [...washLog].reverse().find((w) => w.status === "completed");
      if (!lastCompleted) return showToast("No completed wash to reverse");
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/wash/${lastCompleted.washId}/reverse`, { reasonCode: "superadmin_reversed" }, { headers: authHeader() }));
    } else if (action === "reschedule") {
      const nextScheduled = washLog.find((w) => w.status === "scheduled" || w.status === "due_today");
      if (!nextScheduled) return showToast("No scheduled wash found");
      const newDate = window.prompt("New date (YYYY-MM-DD):");
      if (!newDate) return;
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/wash/${nextScheduled.washId}/reschedule`, { newDate, reasonCode: "superadmin_rescheduled" }, { headers: authHeader() }));
    } else if (action === "compensation") {
      const scheduledDate = window.prompt("Compensation wash date (YYYY-MM-DD):");
      if (!scheduledDate) return;
      runAction(() => axios.post(`${ORDERS_URL}/${id}/compensation-wash`, { scheduledDate, reasonCode: "compensation" }, { headers: authHeader() }));
    } else if (action === "extend") {
      const newEndDate = window.prompt("New end date (YYYY-MM-DD):");
      if (!newEndDate) return;
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/extend`, { newEndDate, reasonCode: "superadmin_extended" }, { headers: authHeader() }));
    } else if (action === "cancel") {
      if (!window.confirm("Cancel this package? This cannot be undone.")) return;
      const note = window.prompt("Cancellation reason:") || "";
      runAction(() => axios.patch(`${ORDERS_URL}/${id}/cancel`, { reasonCode: "superadmin_cancelled", note }, { headers: authHeader() }));
    }
  };

  return (
    <div className="po-app">
      <TopBar />
      <FilterPanel filters={filters} setFilters={setFilters} onSearch={fetchOrders} loading={loading} />
      <OrdersTable orders={orders} selectedId={selectedOrder?._id} onToggle={toggleOrder} loading={loading} />
      {selectedOrder && (
        <OrderDetailPanel
          order={selectedOrder}
          detail={detail}
          detailLoading={detailLoading}
          onAction={handleAction}
          actionLoading={actionLoading}
        />
      )}
      {toast && <div className="po-toast">{toast}</div>}
    </div>
  );
}
